import {
  AvatarGroup,
  Box,
  Divider,
  Flex,
  HStack,
  Spinner,
  Tag,
  type TagProps,
  Text,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type {
  InternalTaskType,
  TaskType,
} from "@src/components/modules/resource-planning/tasks/listing/store";
import { createTimeReportUrl } from "@src/components/modules/time-tracking/Report/time-report-url";
import { BudgetIcon, Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import { timeToHrs } from "@src/components/widgets/Modals/ModalCommunication/ModalCommunication";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { formatDeadline } from "@src/utils/formatDeadline";
import { useStore } from "@src/utils/hooks";
import {
  differenceInDays,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
} from "date-fns";
import { compact, uniqBy } from "lodash";
import { observer } from "mobx-react-lite";
import { now } from "mobx-utils";
import { type FunctionComponent, useMemo } from "react";
import { Avatar } from "../Avatar";
import { DatePicker } from "../datePicker";
import { Link } from "../Link";
import { UncollapseOnHover } from "../UncollapseOnHover";
import { UserTooltip } from "../UserTooltip";

type TDeadlineProps = {
  deadline: TaskType["deadline"];
  dateForColorScheme?: Date | null;
  size?: TagProps["size"];
  fontSize?: TagProps["fontSize"];
  onChange?: (newDeadline: Date | null) => void;
  isLoading?: boolean;
};

type TaskOurWorkBudgetItem = NonNullable<InternalTaskType["ourWorkBudgetItem"]>;

type TAvatarsProps = {
  positions: TaskType["positions"];
};

export function resetTime(date: Date): Date {
  return setMilliseconds(setSeconds(setMinutes(setHours(date, 0), 0), 0), 0);
}

function getDeadlineColor(deadline: string, customDate?: Date | null) {
  const dateToCompare = customDate ?? new Date(now());
  const resetDeadline = resetTime(new Date(deadline));
  const resetDateToCompare = resetTime(dateToCompare);
  const difference = differenceInDays(resetDeadline, resetDateToCompare);

  if (difference < 0) return "red";
  if (difference >= 0 && difference <= 2) return "green";

  return "grey";
}

export const TaskDeadline = observer(function TaskDeadline({
  deadline,
  dateForColorScheme = undefined,
  size = "sm",
  fontSize,
  onChange,
  isLoading,
}: TDeadlineProps) {
  if (!deadline) return <span />;
  const isEditable = !!onChange;

  return (
    <WrapComponent
      if={isEditable}
      with={(children) => (
        <DatePicker
          trigger={children}
          usePortal
          isClearable
          clearButtonLabel={t`Clear deadline`}
          selected={new Date(deadline)}
          onChange={(range) => {
            onChange?.(range?.start ?? null);
          }}
        />
      )}
    >
      <Flex pos="relative">
        <LoadingOverlay
          isLoading={isLoading ?? false}
          position="absolute"
          size="xs"
        >
          <Tag
            fontSize={fontSize}
            whiteSpace="nowrap"
            cursor={isEditable ? "pointer" : undefined}
            colorScheme={getDeadlineColor(deadline, dateForColorScheme)}
            size={size}
          >
            <WrapComponent
              if={isEditable}
              with={(children) => (
                <UncollapseOnHover
                  dataComponentName="deadline-edit-icon"
                  color="inherit"
                  width="3"
                  hiddenComponent={
                    <Icon name="edit-02" h="3" w="3" color="inherit" ml="1" />
                  }
                >
                  <Text>{children}</Text>
                </UncollapseOnHover>
              )}
            >
              {formatDeadline(new Date(deadline))}
            </WrapComponent>
          </Tag>
        </LoadingOverlay>
      </Flex>
    </WrapComponent>
  );
});

type TPlannedBudgetedTimeProps = {
  plannedTime:
    | NonNullable<InternalTaskType["stats"]>["planned_time"]
    | null
    | undefined;
  spentTime: NonNullable<InternalTaskType["stats"]>["spent_time"] | null;
  projectId: TaskOurWorkBudgetItem["project"]["id"] | undefined;
  budgetItemId: TaskOurWorkBudgetItem["id"] | undefined;
  taskId: InternalTaskType["id"] | undefined;
  parentTaskId: InternalTaskType["id"] | undefined;
  isProjectTemplate?: boolean;
  usesBudgetovac:
    | TaskOurWorkBudgetItem["project"]["uses_budgetovac"]
    | undefined;
};
export const TaskPlannedBudgetedTime: FunctionComponent<TPlannedBudgetedTimeProps> =
  observer(function TaskPlannedBudgetedTime({
    plannedTime,
    spentTime,
    projectId,
    budgetItemId,
    taskId,
    parentTaskId,
    usesBudgetovac,
    isProjectTemplate,
  }) {
    const { taskDetailModalStore } = useStore();

    return (
      <HStack spacing="1.5">
        {!isProjectTemplate && (
          <>
            <Flex
              align="center"
              justify="start"
              gap="1"
              minW="max-content"
              textAlign="end"
              onClick={(e) => e.stopPropagation()}
            >
              {spentTime !== null ? (
                <HStack
                  align="center"
                  color={
                    taskDetailModalStore.isOvertime(spentTime, plannedTime)
                      ? "red.500"
                      : spentTime === 0
                        ? "gray.500"
                        : "jade.700"
                  }
                >
                  <TrackedTimeIcon color="inherit" />
                  <Link
                    href={createTimeReportUrl({
                      projectId,
                      budgetItemId,
                      taskId,
                      parentTaskId,
                    })}
                    color={
                      taskDetailModalStore.isOvertime(spentTime, plannedTime)
                        ? "red.500"
                        : spentTime === 0
                          ? "gray.500"
                          : "teal.700"
                    }
                  >
                    <Trans>{timeToHrs(spentTime, 0)}h</Trans>
                  </Link>
                </HStack>
              ) : (
                <Spinner size="sm" />
              )}
            </Flex>
            {/* TODO: Finish after API will be done */}
            {/* <Divider h="3" borderColor="gray.400" orientation="vertical" />
        <Box minW="max-content" textAlign="start" onClick={(e) => e.stopPropagation()}>
        <Link
        href={{
          pathname: '/',
          query: { id: '' },
          }}
          color={0 === 0 ? 'gray.500' : 'teal.700'}
          >
          {timeToHrs(0, 0)}h
          </Link>
          </Box> */}
            <Divider h="3" borderColor="gray.400" orientation="vertical" />
          </>
        )}
        <Box
          minW="max-content"
          textAlign="start"
          onClick={(e) => e.stopPropagation()}
        >
          {plannedTime !== null ? (
            <HStack align="center">
              <BudgetIcon color={spentTime === 0 ? "gray.500" : "jade.700"} />
              <Link
                href={{
                  pathname: usesBudgetovac
                    ? "/projects/budgets"
                    : "/projects/budget",
                  query: { id: projectId },
                }}
                color="initial"
              >
                <Trans>{timeToHrs(plannedTime ?? 0, 0)}h</Trans>
              </Link>
            </HStack>
          ) : (
            <Spinner size="sm" />
          )}
        </Box>
      </HStack>
    );
  });

type TaskPlannedBudgetedBaseProps = {
  projectId: TaskOurWorkBudgetItem["project"]["id"] | undefined;
};

type TaskBudgetedTimeProps = TaskPlannedBudgetedBaseProps & {
  spentTime: NonNullable<InternalTaskType["stats"]>["spent_time"] | null;
  plannedTime: NonNullable<InternalTaskType["stats"]>["planned_time"] | null;
  budgetItemId: TaskOurWorkBudgetItem["id"] | undefined;
  taskId: InternalTaskType["id"] | undefined;
  isProjectTemplate?: boolean;
};
export const TaskBudgetedTime = observer(function TaskBudgetedTime({
  spentTime,
  projectId,
  budgetItemId,
  taskId,
  isProjectTemplate,
  plannedTime,
}: TaskBudgetedTimeProps) {
  const { taskDetailModalStore } = useStore();

  return (
    !isProjectTemplate && (
      <Flex
        align="center"
        justify="start"
        gap="1"
        minW="max-content"
        textAlign="end"
        onClick={(e) => e.stopPropagation()}
      >
        {spentTime !== null ? (
          <HStack
            align="center"
            color={
              taskDetailModalStore.isOvertime(
                spentTime,
                plannedTime ?? undefined,
              )
                ? "red.500"
                : spentTime === 0
                  ? "gray.500"
                  : "jade.700"
            }
          >
            <TrackedTimeIcon color="inherit" />
            <Link
              href={createTimeReportUrl({
                projectId,
                budgetItemId,
                taskId,
              })}
              color={
                taskDetailModalStore.isOvertime(
                  spentTime,
                  plannedTime ?? undefined,
                )
                  ? "red.500"
                  : spentTime === 0
                    ? "gray.500"
                    : "jade.700"
              }
            >
              <Trans>{timeToHrs(spentTime, 0)}h</Trans>
            </Link>
          </HStack>
        ) : (
          <Spinner size="sm" />
        )}
      </Flex>
    )
  );
});

type TaskPlannedTimeProps = TaskPlannedBudgetedBaseProps & {
  plannedTime: NonNullable<InternalTaskType["stats"]>["planned_time"] | null;
  spentTime: NonNullable<InternalTaskType["stats"]>["spent_time"] | null;
  usesBudgetovac:
    | TaskOurWorkBudgetItem["project"]["uses_budgetovac"]
    | undefined;
};
export const TaskPlannedTime = observer(function TaskPlannedTime({
  spentTime,
  plannedTime,
  projectId,
  usesBudgetovac,
}: TaskPlannedTimeProps) {
  const { workspaceStore } = useStore();
  const isOvertime =
    workspaceStore.settings?.highlight_overtime_in_tasks &&
    spentTime != null &&
    plannedTime != null &&
    plannedTime > 0 &&
    spentTime > plannedTime;

  return (
    <Box
      minW="max-content"
      textAlign="start"
      onClick={(e) => e.stopPropagation()}
    >
      {plannedTime !== null ? (
        <HStack align="center">
          <BudgetIcon
            color={
              isOvertime ? "red.500" : spentTime === 0 ? "gray.500" : "jade.700"
            }
          />
          <Link
            href={{
              pathname: usesBudgetovac
                ? "/projects/budgets"
                : "/projects/budget",
              query: { id: projectId },
            }}
            color="initial"
          >
            <Trans>{timeToHrs(plannedTime, 0)}h</Trans>
          </Link>
        </HStack>
      ) : (
        <Spinner size="sm" />
      )}
    </Box>
  );
});

export const TaskAvatars: FunctionComponent<TAvatarsProps> = ({
  positions,
}) => {
  const { authStore } = useStore();
  const uniqUsers = useMemo(
    () =>
      compact(
        uniqBy(
          positions.map((p) => p.user),
          (user) => user?.id,
        ),
      ).toSorted((a, b) =>
        a.id === authStore.user?.id ? -1 : b.id === authStore.user?.id ? 1 : 0,
      ),
    [positions, authStore.user],
  );

  if (!uniqUsers.length) return <Box h="28px" />;

  return (
    <AvatarGroup
      sx={{
        ".chakra-avatar__excess": {
          borderWidth: "1.5px",
          borderColor: "white",
        },
      }}
      minW="100px"
      max={3}
      spacing="-3"
    >
      {uniqUsers.map((u) => (
        <UserTooltip key={u.id} user={u}>
          <Avatar
            colorScheme={u.profile?.hex_color}
            name={u.full_name}
            src={u.image?.urls.thumbnail ?? undefined}
            borderWidth="1.5px"
            borderColor="white"
          />
        </UserTooltip>
      ))}
    </AvatarGroup>
  );
};

type TaskCommentsCountProps = {
  unreadCommentsCount?: number;
  commentsCount: number;
};
export const TaskCommentsCount = observer(function TaskCommentsCount({
  unreadCommentsCount,
  commentsCount,
}: TaskCommentsCountProps) {
  return (
    <HStack justify="end">
      <Text
        color={
          Boolean(unreadCommentsCount) && commentsCount > 0
            ? "white"
            : "grey.600"
        }
        fontSize="xs"
        fontWeight="bold"
        lineHeight="6"
        textAlign="center"
      >
        {commentsCount}
      </Text>
      <Icon
        name="message-text-square-01"
        w="5"
        h="5"
        color={
          Boolean(unreadCommentsCount) && commentsCount > 0
            ? "red.500"
            : "grey.400"
        }
      />
    </HStack>
  );
});
